import { Component, OnInit, CUSTOM_ELEMENTS_SCHEMA, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, AbstractControl, ValidationErrors } from '@angular/forms';
import {
  DialogRef,
  DIALOG_DATA,
  ForgeSelectModule,
  ForgeDatePickerModule,
  ForgeDateRangePickerModule,
  ForgeCircularProgressModule
} from '@tylertech/forge-angular';
import { take } from 'rxjs';
import { AdvancedSearchStateService } from 'src/app/core/services/advanced-search-state.service';
import { AdvancedSearchOption, AdvancedSearchOptions, AdvancedSearchOptionsService, EMPTY_ADVANCED_SEARCH_OPTIONS } from 'src/app/core/services/advanced-search-options.service';
import { FilterOrSearchModel } from 'src/app/models/filter-or-search.model';
import { DateUtils } from 'src/app/utils/date.utils';

@Component({
  selector: 'app-advanced-search-modal',
  templateUrl: './advanced-search-modal.component.html',
  styleUrls: ['./advanced-search-modal.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ForgeSelectModule, ForgeDatePickerModule, ForgeDateRangePickerModule, ForgeCircularProgressModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AdvancedSearchModalComponent implements OnInit {
  protected readonly minDatePickerValue = DateUtils.MIN_DATE_PICKER_VALUE;
  searchForm!: FormGroup;
  ageOptions: number[] = Array.from({ length: 100 }, (_, i) => i + 16);

  // Dropdown options fetched from the API, keyed by filter. Defaults to an empty
  // structure so the template can bind before the request resolves.
  options: Required<AdvancedSearchOptions> = { ...EMPTY_ADVANCED_SEARCH_OPTIONS };
  loading = true;

  // Select controls whose options come from `options` (same key). Used to keep a
  // previously-selected value visible even if it is no longer in the fetched list.
  private readonly optionControls: (keyof AdvancedSearchOptions)[] = [
    'offenderType',
    'supervisionStatus',
    'locationType',
    'facility',
    'countyOfSupervision',
    'convictionCounty',
    'race'
  ];

  constructor(
    private fb: FormBuilder,
    private dialogRef: DialogRef,
    private stateService: AdvancedSearchStateService,
    private advancedSearchOptionsService: AdvancedSearchOptionsService,
    @Inject(DIALOG_DATA) private data: { instanceId?: string } | null
  ) {}

  ngOnInit(): void {
    const initialState = this.stateService.getState(this.data?.instanceId);

    this.searchForm = this.fb.group({
      nameOrId: [initialState.nameOrId || ''],
      offenderType: [initialState.offenderType || ''],
      docNumber: [initialState.docNumber || ''],
      pid: [initialState.pid || ''],
      supervisionStatus: [initialState.supervisionStatus || ''],
      locationType: [initialState.locationType || ''],
      facility: [initialState.facility || ''],
      countyOfSupervision: [initialState.countyOfSupervision || ''],
      minAge: [initialState.minAge || ''],
      maxAge: [initialState.maxAge || ''],
      gender: [initialState.gender || ''],
      race: [initialState.race || ''],
      indictmentNumber: [initialState.indictmentNumber || ''],
      crimeOrOffense: [initialState.crimeOrOffense || ''],
      revisedStatuteCode: [initialState.revisedStatuteCode || ''],
      convictionCounty: [initialState.convictionCounty || ''],
      crimeDate: [DateUtils.formatDateMMDDYYYY(initialState.crimeDate)],
      convictionDate: [DateUtils.formatDateMMDDYYYY(initialState.convictionDate)],
      sentenceStartDate: [DateUtils.formatDateMMDDYYYY(initialState.sentenceStartDate)],
      escapedDateStart: [DateUtils.formatDateMMDDYYYY(initialState.escapedDateStart)],
      escapedDateEnd: [DateUtils.formatDateMMDDYYYY(initialState.escapedDateEnd)],
      projectedReleaseDateStart: [DateUtils.formatDateMMDDYYYY(initialState.projectedReleaseDateStart)],
      projectedReleaseDateEnd: [DateUtils.formatDateMMDDYYYY(initialState.projectedReleaseDateEnd)]
    }, { validators: [this.formValidator.bind(this)] });

    this.loadOptions();
  }

  private loadOptions(): void {
    const instanceId = this.data?.instanceId;
    if (!instanceId) {
      this.loading = false;
      return;
    }

    this.advancedSearchOptionsService.getAdvancedSearchOptions(instanceId)
      .pipe(take(1))
      .subscribe(options => {
        this.options = { ...EMPTY_ADVANCED_SEARCH_OPTIONS, ...options };
        this.ensureSelectedValuesPresent();
        this.loading = false;
      });
  }

  // Keep a previously-selected value visible even when the fetched list does not
  // contain it (e.g. the underlying data was removed): append it so the
  // forge-select still renders the current selection.
  private ensureSelectedValuesPresent(): void {
    for (const key of this.optionControls) {
      const value = this.searchForm.get(key)?.value;
      const list = this.options[key] ?? [];
      if (value && !list.some((option: AdvancedSearchOption) => option.value === value)) {
        this.options[key] = [...list, { value, label: value }];
      }
    }
  }

  formValidator(control: AbstractControl): ValidationErrors | null {
    const errors: ValidationErrors = {};

    // Age Range
    const minAge = control.get('minAge')?.value;
    const maxAge = control.get('maxAge')?.value;
    if (minAge !== null && minAge !== '' && maxAge !== null && maxAge !== '') {
      if (Number(minAge) > Number(maxAge)) {
        errors.ageRangeInvalid = true;
      }
    }

    // Escaped Date
    const escapedStart = control.get('escapedDateStart')?.value;
    const escapedEnd = control.get('escapedDateEnd')?.value;
    if (escapedStart && escapedEnd) {
      const start = new Date(escapedStart);
      const end = new Date(escapedEnd);
      if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);
        if (start > end) {
          errors.escapedDateInvalid = true;
        }
      }
    }

    // Projected Release Date
    const projectedStart = control.get('projectedReleaseDateStart')?.value;
    const projectedEnd = control.get('projectedReleaseDateEnd')?.value;
    if (projectedStart && projectedEnd) {
      const start = new Date(projectedStart);
      const end = new Date(projectedEnd);
      if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);
        if (start > end) {
          errors.projectedReleaseDateInvalid = true;
        }
      }
    }

    return Object.keys(errors).length > 0 ? errors : null;
  }

  onDateRangeChange(): void {
    this.searchForm.updateValueAndValidity();
  }

  syncDate(controlName: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchForm.get(controlName)?.setValue(input.value, { emitEvent: true });
  }

  closeDialog(): void {
    this.dialogRef.close(null);
  }

  onSubmit(): void {
    if (this.searchForm.invalid) {
      this.searchForm.markAllAsTouched();
      return;
    }
    const formValue = { ...this.searchForm.value };

    const formatDateToIso = (val: any): string | null => {
      if (!val) return null;
      if (val instanceof Date && !isNaN(val.getTime())) {
        const yyyy = val.getFullYear();
        const mm = String(val.getMonth() + 1).padStart(2, '0');
        const dd = String(val.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      }
      if (typeof val === 'string') {
        const trimmed = val.trim();
        const slashParts = trimmed.split('/');
        if (slashParts.length === 3) {
          const month = slashParts[0].padStart(2, '0');
          const day = slashParts[1].padStart(2, '0');
          const year = slashParts[2];
          if (year.length === 4) {
            return `${year}-${month}-${day}`;
          }
        }
        const dashParts = trimmed.split('-');
        if (dashParts.length === 3 && dashParts[0].length <= 2 && dashParts[2].length === 4) {
          const month = dashParts[0].padStart(2, '0');
          const day = dashParts[1].padStart(2, '0');
          const year = dashParts[2];
          return `${year}-${month}-${day}`;
        }
        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
          return trimmed;
        }
      }
      return val;
    };

    if (formValue.crimeDate) formValue.crimeDate = formatDateToIso(formValue.crimeDate);
    if (formValue.convictionDate) formValue.convictionDate = formatDateToIso(formValue.convictionDate);
    if (formValue.sentenceStartDate) formValue.sentenceStartDate = formatDateToIso(formValue.sentenceStartDate);
    if (formValue.escapedDateStart) formValue.escapedDateStart = formatDateToIso(formValue.escapedDateStart);
    if (formValue.escapedDateEnd) formValue.escapedDateEnd = formatDateToIso(formValue.escapedDateEnd);
    if (formValue.projectedReleaseDateStart) formValue.projectedReleaseDateStart = formatDateToIso(formValue.projectedReleaseDateStart);
    if (formValue.projectedReleaseDateEnd) formValue.projectedReleaseDateEnd = formatDateToIso(formValue.projectedReleaseDateEnd);

    // Save state
    this.stateService.setState(formValue, this.data?.instanceId);

    // Close with data
    this.dialogRef.close(formValue);
  }
}